// src/pages/TrangCuaToi.js
import React, { useState, useEffect, useCallback } from "react";
import { fetchData, addData, updateData, deleteData } from "../services/api";
import { useThongBao, ThongBao, DangTai, docQuyen, hoiXoa } from "../vo";

// ============================================================
// 💡 TRANG CỦA TÔI = HỘP ĐỀ XUẤT (Đợt N3, 11/09/2026)
// ------------------------------------------------------------
// Trước đợt này trang này là ba ô kiểu cũ (việc riêng · hóa đơn · công thức)
// — nửa còn lại của "cái gương" thế hệ 1. Chủ nhà chốt 08/09:
//
//   *"Trang của tôi: tôi muốn làm gì tôi làm, tôi muốn cho ai thấy cái gì
//    là quyền của tôi… trên trang này, mọi người sẽ đề xuất thêm mục gì,
//    cái mình muốn — dạng trang phát triển mà không ảnh hưởng lớn đến
//    toàn cục."*
//
// Đợt này dựng phần rẻ nhất mà dùng được ngay: hộp đề xuất. Mockup anh
// duyệt 11/09 (mockup/hop-de-xuat.html, ngoài kho). Khu riêng đầy đủ để
// "sau khi chạy thử" — docs/LO_TRINH.md, cuối file.
//
// ⚠️ NHỮNG CHỖ CỐ Ý — đừng "sửa cho giống app khác":
//   • KHÔNG CON SỐ. Hiện TÊN người cũng muốn, như hàng "Hôm nay có mặt" của
//     Điểm danh. Chân dung 5.4: thành viên không thấy con số nào.
//   • Nút ghi "Tôi cũng muốn", không ghi "Thích" — câu người ta nghĩ trong đầu.
//   • MỚI NHẤT lên trên (máy chủ xếp), không xếp theo lượt bấm — không thành
//     cuộc thi đề xuất của ai "thắng".
//   • KHÔNG bình luận — bước đầu tiên về phía "chat" (chân dung 4.7).
//   • Ai đăng nhập cũng viết được, không ô quyền nào (bài học Lịch 08/09).
//
// ⚠️ MỌI COMPONENT CON NẰM Ở CẤP NGOÀI CÙNG CỦA FILE, không bao giờ trong
// một component khác. Hàm nằm trong hàm thì mỗi lần vẽ lại là một hàm MỚI,
// React tháo ô nhập ra dựng lại → mất con trỏ giữa lúc gõ.
// Xem docs/bai-hoc/2026-09-07-06_ham-trong-ham-lam-mat-con-tro.md
// ============================================================

const TOI_DA = 500; // khớp máy chủ và CHECK trong migration 004

// "hôm nay" / "hôm qua" / ngày. Máy chủ đã tính theo giờ Việt Nam và gửi
// sẵn cả chuỗi lẫn số ngày — ở đây KHÔNG dùng new Date() (bài học 03).
const veNgay = (dx) => {
  if (dx.so_ngay_truoc === 0) return "hôm nay";
  if (dx.so_ngay_truoc === 1) return "hôm qua";
  return dx.ngay_viet || "";
};

// "Mẹ, Anh hai Bi cũng muốn" — CHỈ NGƯỜI KHÁC. Phần của mình đã có nút
// "✔ Bạn cũng muốn" nói rồi. Bản đầu (theo mockup) ghi cả "Bạn" vào dòng
// này; thử thật 11/09 thấy nó thành "✔ Bạn cũng muốn | Bạn cũng muốn" —
// một câu nói hai lần sát nhau.
const veTenCungMuon = (ds, toiId) =>
  ds
    .filter((n) => n.id !== toiId)
    .map((n) => n.full_name)
    .join(", ");

function DongDeXuat({
  dx,
  toiId,
  suaDuoc,
  dangSua,
  noiDungSua,
  onDoiNoiDungSua,
  onMoSua,
  onLuuSua,
  onHuySua,
  onXoa,
  onDoiMuon,
  dangBam,
}) {
  const ds = Array.isArray(dx.nguoi_cung_muon) ? dx.nguoi_cung_muon : [];
  const toiCungMuon = ds.some((n) => n.id === toiId);
  const ten = veTenCungMuon(ds, toiId);

  if (dangSua) {
    return (
      <li className="dx-dong dx-dong-sua">
        <textarea
          value={noiDungSua}
          onChange={(e) => onDoiNoiDungSua(e.target.value)}
          className="dx-o"
          maxLength={TOI_DA}
          rows={3}
          autoFocus
        />
        <div className="dx-hang-nut">
          <button type="button" onClick={onLuuSua} className="nut nut-xanh">
            Lưu
          </button>
          <button type="button" onClick={onHuySua} className="nut nut-xam">
            Thôi
          </button>
        </div>
      </li>
    );
  }

  return (
    <li className="dx-dong">
      <p className="dx-noi-dung">{dx.noi_dung}</p>
      <p className="dx-ai">
        {dx.ten_nguoi_viet || "Ai đó"} · {veNgay(dx)}
        {dx.da_sua && <span className="dx-da-sua"> · đã sửa</span>}
      </p>
      <div className="dx-hang-thich">
        <button
          type="button"
          className={toiCungMuon ? "dx-thich dx-thich-da" : "dx-thich"}
          onClick={onDoiMuon}
          disabled={dangBam}
          aria-pressed={toiCungMuon}
          title={toiCungMuon ? "Bấm lần nữa để thôi" : "Bấm nếu bạn cũng muốn điều này"}
        >
          {toiCungMuon ? "✔ Bạn cũng muốn" : "👍 Tôi cũng muốn"}
        </button>
        {ten && (
          <span className="dx-ten-thich">
            <strong>{ten}</strong> {toiCungMuon ? "cũng vậy" : "cũng muốn"}
          </span>
        )}
        {suaDuoc && (
          <span className="dx-nut-sua">
            <button type="button" onClick={onMoSua} className="btn-edit" title="Sửa">
              ✏️
            </button>
            <button type="button" onClick={onXoa} className="btn-delete" title="Xóa">
              🗑️
            </button>
          </span>
        )}
      </div>
    </li>
  );
}

function TrangCuaToi({ user }) {
  const [ds, setDs] = useState([]);
  const [dangTai, setDangTai] = useState(true);
  const [moi, setMoi] = useState("");
  const [dangGui, setDangGui] = useState(false);
  const [suaId, setSuaId] = useState(null);
  const [noiDungSua, setNoiDungSua] = useState("");
  const [dangBamId, setDangBamId] = useState(null);

  const { laAdmin } = docQuyen(user);
  const { bao, noiBao } = useThongBao();

  const taiLai = useCallback(async () => {
    try {
      const d = await fetchData("de-xuat");
      setDs(Array.isArray(d) ? d : []);
    } catch (err) {
      noiBao("⚠️ Không tải được hộp đề xuất");
    } finally {
      setDangTai(false);
    }
  }, [noiBao]);

  useEffect(() => {
    taiLai();
  }, [taiLai]);

  const gui = async (e) => {
    if (e) e.preventDefault();
    const noiDung = moi.trim();
    if (noiDung === "") return;
    setDangGui(true);
    try {
      await addData("de-xuat", { noi_dung: noiDung });
      setMoi("");
      await taiLai();
      // Không hiện "đã gửi" — đề xuất hiện ngay trên đầu danh sách rồi.
    } catch (err) {
      noiBao(err.chiTiet ? `⚠️ ${err.chiTiet}` : "⚠️ Chưa gửi được, thử lại giúp nhé");
    } finally {
      setDangGui(false);
    }
  };

  // Hai đường riêng ở máy chủ: "cũng muốn" và "thôi muốn". Bấm lại bao
  // nhiêu lần cũng ra cùng một kết quả, không có kiểu đảo qua đảo lại.
  const doiMuon = async (dx) => {
    const toiCungMuon = (dx.nguoi_cung_muon || []).some((n) => n.id === user.id);
    setDangBamId(dx.id);
    try {
      await updateData("de-xuat", `${dx.id}/${toiCungMuon ? "thoi-muon" : "cung-muon"}`, {});
      await taiLai();
    } catch (err) {
      noiBao("⚠️ Chưa ghi nhận được, thử lại giúp nhé");
    } finally {
      setDangBamId(null);
    }
  };

  const moSua = (dx) => {
    setSuaId(dx.id);
    setNoiDungSua(dx.noi_dung || "");
  };

  const luuSua = async (dx) => {
    const noiDung = noiDungSua.trim();
    if (noiDung === "") {
      noiBao("⚠️ Đề xuất không được để trống — muốn bỏ thì bấm 🗑️");
      return;
    }
    try {
      await updateData("de-xuat", dx.id, { noi_dung: noiDung });
      setSuaId(null);
      await taiLai();
    } catch (err) {
      noiBao(err.chiTiet ? `⚠️ ${err.chiTiet}` : "⚠️ Chưa sửa được");
    }
  };

  const xoa = async (dx) => {
    if (!hoiXoa("đề xuất", dx.noi_dung)) return;
    try {
      await deleteData("de-xuat", dx.id);
      await taiLai();
    } catch (err) {
      noiBao("⚠️ Không xóa được — chỉ người viết mới xóa được đề xuất này");
    }
  };

  if (dangTai) return <DangTai gi="hộp đề xuất" />;

  return (
    <div className="card dx-khung">
      <h3>💡 Hộp đề xuất</h3>
      <p className="dx-mo-ta">
        Muốn app có thêm gì, bớt gì, đổi gì — viết vào đây. Cả nhà cùng đọc.
      </p>
      <ThongBao bao={bao} />

      {/* Ô viết nằm SẴN trên đầu, như trang Đi chợ — không giấu sau nút ➕.
          Enter trong ô này là XUỐNG DÒNG (đề xuất có thể dài vài câu);
          gửi bằng nút. */}
      <form onSubmit={gui} className="dx-form">
        <textarea
          value={moi}
          onChange={(e) => setMoi(e.target.value)}
          placeholder="Tôi muốn app có thêm…"
          className="dx-o"
          maxLength={TOI_DA}
          rows={3}
        />
        <div className="dx-hang-gui">
          {moi.length > TOI_DA - 50 && (
            <span className="dx-dem-chu">còn {TOI_DA - moi.length} chữ cái</span>
          )}
          <button
            type="submit"
            className="nut nut-xanh"
            disabled={dangGui || moi.trim() === ""}
          >
            {dangGui ? "Đang gửi…" : "Gửi đề xuất"}
          </button>
        </div>
      </form>

      {ds.length === 0 ? (
        <p className="dx-trong">
          Chưa có đề xuất nào. Thấy app thiếu gì, thừa gì, khó dùng chỗ nào — viết dòng
          đầu tiên ở ô trên.
        </p>
      ) : (
        <ul className="dx-ds">
          {ds.map((dx) => (
            <DongDeXuat
              key={dx.id}
              dx={dx}
              toiId={user.id}
              suaDuoc={laAdmin || dx.nguoi_viet === user.id}
              dangSua={suaId === dx.id}
              noiDungSua={noiDungSua}
              onDoiNoiDungSua={setNoiDungSua}
              onMoSua={() => moSua(dx)}
              onLuuSua={() => luuSua(dx)}
              onHuySua={() => setSuaId(null)}
              onXoa={() => xoa(dx)}
              onDoiMuon={() => doiMuon(dx)}
              dangBam={dangBamId === dx.id}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

export default TrangCuaToi;
